/**
 * Featured Properties Carousel
 * ----------------------------
 * Home-page snippet that surfaces up to N featured real estate listings.
 * Renders nothing until the fetch resolves, and renders nothing if no
 * featured properties exist — keeping the home page tidy by default.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouseUser,
  faChevronLeft,
  faChevronRight,
  faBed,
  faBath,
  faRulerCombined,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchRealEstateListings,
  getPropertyHeroImage,
  formatPrice,
  propertyHref,
} from "../../utils/realEstate";
import { SmartImage } from "../OptimizedImage";
import "../../styles/FeaturedProperties.css";

export default function FeaturedPropertiesCarousel({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchRealEstateListings({ includeInactive: false });
        const featured = all.filter((p) => p.featured).slice(0, limit);
        if (!cancelled) setItems(featured);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const nudge = (dx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="featured-properties" aria-label="Featured properties">
      <div className="featured-properties__head">
        <div>
          <span className="featured-properties__eyebrow">· Treasure on the market ·</span>
          <h2><FontAwesomeIcon icon={faHouseUser} /> Featured properties</h2>
          <p>Hand-picked island homes, condos, and land curated by the Guild's real estate desk.</p>
        </div>
        <div className="featured-properties__controls">
          <button onClick={() => nudge(-360)} aria-label="Scroll left" title="Previous properties"><FontAwesomeIcon icon={faChevronLeft} /></button>
          <button onClick={() => nudge(360)}  aria-label="Scroll right" title="Next properties"><FontAwesomeIcon icon={faChevronRight} /></button>
        </div>
      </div>
      <div className="featured-properties__scroller" ref={scrollerRef}>
        {items.map((p) => {
          const img = getPropertyHeroImage(p);
          return (
            <Link key={p.id} to={propertyHref(p)} className="featured-property-card">
              {img ? (
                <div className="featured-property-card__image">
                  <SmartImage
                    src={img}
                    alt={p.title || "Property photo"}
                    className="featured-property-card__image-inner"
                    objectFit="cover"
                  />
                </div>
              ) : (
                <div className="featured-property-card__image featured-property-card__image--placeholder" />
              )}
              <div className="featured-property-card__price">{formatPrice(p)}</div>
              <div className="featured-property-card__body">
                <h3>{p.title}</h3>
                {p.address && <p className="featured-property-card__address">{p.address}</p>}
                <ul className="featured-property-card__specs">
                  {p.beds != null  && <li><FontAwesomeIcon icon={faBed} /> {p.beds} bd</li>}
                  {p.baths != null && <li><FontAwesomeIcon icon={faBath} /> {p.baths} ba</li>}
                  {p.sqft != null  && <li><FontAwesomeIcon icon={faRulerCombined} /> {p.sqft.toLocaleString()} sqft</li>}
                </ul>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="featured-properties__footer">
        <Link to="/properties" className="featured-properties__cta">See all properties →</Link>
      </div>
    </section>
  );
}
